/* ============================================
   Artemis Theme — Designer Logo Library
   ============================================
   Multi-logo upload at page level. Each upload
   lands either on the configured Worker (R2) or
   in localStorage as a data URL when no service
   is set. Each entry in the designer picks which
   library logo to apply.

   Accepts: png, jpg, svg, pdf, ai, eps (5MB max).
   Vector files cannot render in the canvas
   preview, but their URL still attaches to cart
   line item properties for fulfillment.
   ============================================ */

(function () {
  'use strict';

  if (!window.DesignerSession) return;

  var rootEl = document.querySelector('[data-designer-logo]');
  if (!rootEl) return;

  var designerEl = document.querySelector('[data-designer]');
  var uploadServiceUrl = designerEl ? (designerEl.getAttribute('data-upload-url') || '').replace(/\/+$/, '') : '';

  var fileInput = rootEl.querySelector('[data-logo-file]');
  var uploadBtn = rootEl.querySelector('[data-logo-upload-btn]');
  var listEl = rootEl.querySelector('[data-logo-list]');
  var emptyEl = rootEl.querySelector('[data-logo-empty]');
  var statusEl = rootEl.querySelector('[data-logo-status]');

  var MAX_BYTES = 5 * 1024 * 1024;

  var EXT_TO_MIME = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    pdf: 'application/pdf',
    ai: 'application/illustrator',
    eps: 'application/postscript',
  };

  var ALLOWED_EXT = Object.keys(EXT_TO_MIME);

  // MIME → extension, so the type chip stays correct after a logo is renamed
  // (the editable name no longer carries a reliable file extension).
  var MIME_TO_EXT = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/svg+xml': 'svg',
    'image/webp': 'webp',
    'application/pdf': 'pdf',
    'application/illustrator': 'ai',
    'application/vnd.adobe.illustrator': 'ai',
    'application/postscript': 'eps',
  };

  function getExt(name) {
    var parts = String(name || '').split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  function logoExt(logo) {
    return MIME_TO_EXT[logo.contentType] || getExt(logo.name);
  }

  function detectMime(file) {
    var ext = getExt(file.name);
    if (file.type && file.type !== 'application/octet-stream') return file.type;
    return EXT_TO_MIME[ext] || 'application/octet-stream';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function setStatus(message, type) {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.className = 'designer-logo__status';
    if (type) statusEl.classList.add('designer-logo__status--' + type);
  }

  function readAsDataUrl(file) {
    var contentType = detectMime(file);
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        resolve({
          url: reader.result,
          previewUrl: reader.result,
          key: '',
          contentType: contentType,
          name: file.name,
          width: null,
          height: null,
          isVector: null,
          colorCount: null,
        });
      };
      reader.onerror = function () { reject(new Error('Could not read file')); };
      reader.readAsDataURL(file);
    });
  }

  function uploadToR2(file) {
    var contentType = detectMime(file);
    return fetch(uploadServiceUrl + '/upload', {
      method: 'POST',
      headers: { 'Content-Type': contentType },
      body: file,
    }).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (t) { throw new Error('Upload failed (' + res.status + '): ' + t); });
      }
      return res.json().then(function (json) {
        return {
          url: json.url,
          previewUrl: json.previewUrl || json.url,
          key: json.key,
          contentType: contentType,
          name: file.name,
          width: typeof json.width === 'number' ? json.width : null,
          height: typeof json.height === 'number' ? json.height : null,
          isVector: typeof json.isVector === 'boolean' ? json.isVector : null,
          colorCount: typeof json.colorCount === 'number' ? json.colorCount : null,
        };
      });
    });
  }

  function persistLogo(file) {
    if (!uploadServiceUrl) return readAsDataUrl(file);
    return uploadToR2(file);
  }

  // Tolerance for Cloudinary's e_make_transparent — also catches the near-white
  // anti-alias ramp around the page edge, not just pure #fff.
  var WHITE_KNOCKOUT_TOLERANCE = 30;

  /* Decode `url` into a small RGBA buffer for analysis. Runs on the Cloudinary
     preview PNG, which is a rasterised PNG for every source format (PDF/AI/EPS
     included) and is CORS-clean, so getImageData works. Nearest-neighbour
     downscale — smooth scaling averages neighbouring pixels, blending a
     1-colour design into a spread of greys and over-counting, so we sample real
     pixels to keep solid ink solid. Resolves null on any failure. */
  function loadImageData(url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var MAX = 200;
          var natW = img.naturalWidth || img.width;
          var natH = img.naturalHeight || img.height;
          var scale = Math.min(1, MAX / Math.max(natW, natH));
          var w = Math.max(1, Math.round(natW * scale));
          var h = Math.max(1, Math.round(natH * scale));
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.imageSmoothingEnabled = false;
          ctx.mozImageSmoothingEnabled = false;
          ctx.webkitImageSmoothingEnabled = false;
          ctx.msImageSmoothingEnabled = false;
          ctx.drawImage(img, 0, 0, w, h);
          resolve({ data: ctx.getImageData(0, 0, w, h).data, w: w, h: h });
        } catch (err) {
          resolve(null); // tainted canvas / decode failure
        }
      };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  /* Quantized spot-colour estimate over an RGBA buffer. Each channel is reduced
     to 8 levels (512-bucket histogram), then we count histogram *peaks*: a
     quantised colour is a distinct ink only when no neighbouring bucket holds
     more pixels. Anti-alias edges (white→ink, ink→ink) are monotonic ramps with
     no local maximum, so their intermediate greys never count — only the solid
     fills at the ramp ends do. This is what stops a single block of black text
     from reading as ~8 colours just because its edges fade to grey. Buckets
     under 0.5% of opaque pixels are dropped first as stray fringe. Returns null
     when nothing is opaque so the badge just hides. */
  function countColors(data) {
    var hist = {};
    var opaque = 0;
    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue; // skip (semi-)transparent pixels
      opaque++;
      var key = ((data[i] >> 5) << 6) | ((data[i + 1] >> 5) << 3) | (data[i + 2] >> 5);
      hist[key] = (hist[key] || 0) + 1;
    }
    if (!opaque) return null;
    var threshold = opaque * 0.005;

    function at(r, g, b) {
      if (r < 0 || r > 7 || g < 0 || g > 7 || b < 0 || b > 7) return -1;
      return hist[(r << 6) | (g << 3) | b] || 0;
    }

    var count = 0;
    for (var k in hist) {
      var n = hist[k];
      if (n < threshold) continue;
      var kc = +k;
      var r = (kc >> 6) & 7, g = (kc >> 3) & 7, b = kc & 7;
      var isPeak = true;
      for (var dr = -1; dr <= 1 && isPeak; dr++) {
        for (var dg = -1; dg <= 1 && isPeak; dg++) {
          for (var db = -1; db <= 1; db++) {
            if (dr === 0 && dg === 0 && db === 0) continue;
            if (at(r + dr, g + dg, b + db) > n) { isPeak = false; break; }
          }
        }
      }
      if (isPeak) count++;
    }
    return count || 1;
  }

  /* True when the image border is a solid opaque near-white field — i.e. a
     rasterised page (a PDF/PNG whose background is white rather than
     transparent). We sample ONLY the border: a transparent logo has
     transparent edges and a colour-background logo has non-white edges, so
     neither trips this — which is what keeps the white knockout below from
     punching holes in artwork that legitimately contains white. */
  function hasOpaqueWhiteField(data, w, h) {
    var border = 0;
    var whiteOpaque = 0;
    function sample(x, y) {
      var p = (y * w + x) * 4;
      border++;
      if (data[p + 3] >= 250 && data[p] >= 245 && data[p + 1] >= 245 && data[p + 2] >= 245) {
        whiteOpaque++;
      }
    }
    var x, y;
    for (x = 0; x < w; x++) { sample(x, 0); sample(x, h - 1); }
    for (y = 1; y < h - 1; y++) { sample(0, y); sample(w - 1, y); }
    return border > 0 && (whiteOpaque / border) >= 0.6;
  }

  /* Inject e_make_transparent into a Cloudinary fetch URL's transform segment
     so the rasterised white page is knocked out to transparency. Returns null
     for anything that isn't a Cloudinary fetch URL (data: URLs, raw R2) — there
     is nothing to rewrite, so the caller leaves the preview untouched. */
  function makeTransparentVariant(url) {
    var marker = '/image/fetch/';
    var at = url.indexOf(marker);
    if (at < 0) return null;
    var head = url.slice(0, at + marker.length);
    var rest = url.slice(at + marker.length);
    var slash = rest.indexOf('/'); // transforms end at the first slash before the source URL
    if (slash < 0) return null;
    var transforms = rest.slice(0, slash);
    if (transforms.indexOf('e_make_transparent') >= 0) return url;
    return head + transforms + ',e_make_transparent:' + WHITE_KNOCKOUT_TOLERANCE + rest.slice(slash);
  }

  /* Set logo.colorCount, and when the preview is a white-page raster, swap
     logo.previewUrl to a knocked-out (transparent) Cloudinary variant so the
     canvas stops showing a white card behind the artwork. The count is then
     taken from that variant, so it reflects what actually prints (black text on
     a white page → 1 colour, not ~10 from the white field + anti-alias greys).
     logo.url (the original vector sent to the cart) is never touched. */
  function analyzeLogo(logo) {
    var src = logo.previewUrl || logo.url;
    return loadImageData(src).then(function (info) {
      if (!info) return logo; // tainted / undecodable (e.g. PDF data: URL) — leave as-is
      if (hasOpaqueWhiteField(info.data, info.w, info.h)) {
        var variant = makeTransparentVariant(src);
        if (variant) {
          return loadImageData(variant).then(function (vinfo) {
            if (vinfo) {
              logo.previewUrl = variant; // only commit the swap once the variant decodes
              logo.colorCount = countColors(vinfo.data);
            } else {
              logo.colorCount = countColors(info.data);
            }
            return logo;
          });
        }
      }
      logo.colorCount = countColors(info.data);
      return logo;
    });
  }

  function handleFile(file) {
    var ext = getExt(file.name);
    if (ALLOWED_EXT.indexOf(ext) < 0) {
      setStatus('Unsupported file type. Use PNG, JPG, SVG, PDF, AI, or EPS.', 'error');
      return;
    }
    if (file.size > MAX_BYTES) {
      setStatus('File must be under 5MB.', 'error');
      return;
    }
    setStatus(uploadServiceUrl ? 'Uploading…' : 'Loading…', '');
    persistLogo(file)
      // Colour count (and white-page knockout) is computed client-side from the
      // preview PNG so it works for every format; width/height/isVector come
      // from the service.
      .then(analyzeLogo)
      .then(function (logo) {
        window.DesignerSession.addPageLogo(logo);
        setStatus('Added.', 'success');
      })
      .catch(function (err) {
        setStatus(err.message || 'Upload failed.', 'error');
      });
  }

  function renderRow(logo) {
    var ext = logoExt(logo);
    var src = logo.previewUrl || logo.url;

    var li = document.createElement('li');
    li.className = 'designer-logo__item';
    li.setAttribute('data-logo-id', logo.id);
    li.innerHTML =
      '<div class="designer-logo__item-media">' +
        '<img src="' + escapeHtml(src) + '" alt="" crossorigin="anonymous">' +
      '</div>' +
      '<div class="designer-logo__item-info">' +
        '<input type="text" class="designer-logo__item-name" data-logo-name ' +
          'value="' + escapeHtml(logo.name) + '" aria-label="Logo name" ' +
          'title="Rename this logo">' +
        '<span class="designer-logo__item-type">' + escapeHtml(ext.toUpperCase()) + '</span>' +
      '</div>' +
      '<button type="button" class="designer-logo__item-remove" data-logo-remove aria-label="Remove logo">' +
        '×' +
      '</button>';

    li.querySelector('[data-logo-remove]').addEventListener('click', function () {
      window.DesignerSession.removePageLogo(logo.id);
    });

    // Editable display name — commit on blur / Enter; revert if left blank.
    var nameInput = li.querySelector('[data-logo-name]');
    nameInput.addEventListener('change', function () {
      var next = nameInput.value.trim();
      if (!next) { nameInput.value = logo.name; return; }
      if (next !== logo.name) window.DesignerSession.renamePageLogo(logo.id, next);
    });
    nameInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') nameInput.blur();
    });

    return li;
  }

  function renderList(logos) {
    listEl.innerHTML = '';
    if (!logos || logos.length === 0) {
      emptyEl.style.display = '';
      listEl.appendChild(emptyEl);
      return;
    }
    emptyEl.style.display = 'none';
    logos.forEach(function (logo) { listEl.appendChild(renderRow(logo)); });
  }

  if (uploadBtn && fileInput) {
    uploadBtn.addEventListener('click', function () { fileInput.click(); });
    fileInput.addEventListener('change', function () {
      var file = fileInput.files[0];
      if (file) handleFile(file);
      fileInput.value = '';
    });
  }

  renderList(window.DesignerSession.getPageLogos());
  window.DesignerSession.on('pageLogos', renderList);
})();
